/**
 * Create events table with light mode optimization.
 * Displays tournament events with conditional data loading based on event count threshold.
 */
import { headerSortElement } from '../common/sorters/headerSortElement';
import { mapEvent } from 'pages/tournament/tabs/eventsTab/mapEvent';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { destroyTipster } from 'components/popovers/tipster';
import { destroyTable } from 'pages/tournament/destroyTable';
import { tournamentEngine } from 'services/factory/engine';
import { displayConfig } from 'config/displayConfig';
import { getEventColumns } from './getEventColumns';

// constants
import { TOURNAMENT_EVENTS } from 'constants/tmxConstants';
import { t } from 'i18n';

const EVENT_COUNT_THRESHOLD = 15;

export function createEventsTable(): { table: any; replaceTableData: () => void } {
  let table: any;

  const initialEventData = tournamentEngine.getEvents({ withScaleValues: false });
  const eventCount = initialEventData?.events?.length || 0;
  const lightMode = eventCount > EVENT_COUNT_THRESHOLD;

  const getTableData = () => {
    const eventData = tournamentEngine.getEvents({ withScaleValues: !lightMode });

    return (
      eventData?.events?.map((event: any) =>
        mapEvent({
          scaleValues: eventData.eventScaleValues?.[event.eventId],
          lightMode,
          event,
        }),
      ) || []
    );
  };

  const replaceTableData = () => {
    table.replaceData(getTableData());
  };

  const columns = getEventColumns(() => lightMode);

  const render = (data: any[]) => {
    destroyTable({ anchorId: TOURNAMENT_EVENTS });
    const element = document.getElementById(TOURNAMENT_EVENTS)!;
    // Header text is owned by the orchestrator (`eventsView.ts`) so the same
    // line can carry the cards/table view toggle.

    table = new Tabulator(element, {
      columnDefaults: {},
      headerSortElement: headerSortElement([
        'scheduledMatchUpsCount',
        'completedMatchUpsCount',
        'matchUpsCount',
        'entriesCount',
        'drawsCount',
      ]),
      height: window.innerHeight * (displayConfig.get().tableHeightMultiplier ?? 0.85),
      placeholder: t('pages.participants.noEvents'),
      layout: 'fitColumns',
      reactiveData: true,
      index: 'eventId',
      columns,
      data,
    });
    table.on('scrollVertical', destroyTipster);
    // Header count updates are owned by the orchestrator via dataChanged /
    // dataFiltered subscriptions on the returned table.
  };

  render(getTableData());

  return { table, replaceTableData };
}
